/* eslint-disable complexity */
import React, {
  useState,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react';
import * as yup from 'yup';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import FullSizeProgress from '../../../Components/Progress/FullSize/FullSizeProgress';
import ModalProgress from '../../../Components/Progress/Modal/ModalProgress';
import Title from '../../../Components/Labels/Title';
import TextInput from '../../../Components/Inputs/TextInput';
import BaseButton from '../../../Components/Buttons/BaseButton';
import YesNoButton from '../../../Components/Buttons/YesNoButton';

import QuestionMark from '../../../Components/Adornments/QuestionMark';
import InvoiceHelpDialog from '../../../Components/Dialogs/InvoiceHelpDialog/InvoiceHelpDialog';

import FooterPayment from '../../Payment/FooterPayment';
import ContractCard from '../../../Components/Cards/ContractCard';

import { history } from '../../../Routes/history';
import {
  UserContext,
  UserDispatchContext
} from '../../../Contexts/UserContext';
import {
  CONTRACT_RELATIONSHIPS,
  CONTRACT_TYPES,
  TaskQuestionType,
  ChallengeStatus,
  formatAnswerDataBody,
  getDefaultContractAlias,
  getDefaultContractIcon,
  areTasksValid,
  isChallengeTypeValid,
  isDigitalInvoiceAllowedByContractType
} from '../contract_enums';
import { AlertsDispatchContext } from '../../../Contexts/AlertsContext';
import { HELP_TEXT } from '../../../Utils/enums';

// * API
import {
  GetContractChallengeAPI,
  GetSingleContractAPI,
  PostAssociateContract
} from '../../../API/Contracts/ContractsAPI';
import {
  redirectOnAuthFailure,
  extractErrorMessage,
  extractErrorCode
} from '../../../Utils/Errors/Errors';
import {
  ContractsContext,
  ContractsRefreshContext
} from '../../../Contexts/ContractsContext';
import RequestDialog from '../../../Components/Dialogs/RequestDialog';
import PhoneVerificationDialog from '../../Profile/PhoneVerificationDialog';
import { ResendPhoneConfirmationAPI } from '../../../API/UserAPI';
import RecaptchaDisclaimer from '../../../Components/Captcha/TurnstileDisclaimer';
import TurnstileInvisibleCaptcha from '../../../Components/Captcha/TurnstileInvisibleCaptcha';
import { RecaptchaAction } from '../../../Enums/recaptcha';
import ContractAssociationForm from './ContractAssociationForm/ContractAssociationForm';
import { Company } from '../../../Configs/general';
import SelectInput from '../../../Components/Inputs/SelectInput';
import ContractChallenge from '../ContractAssociation/ContractChallenge/ContractChallenge';
import VerticalLinearStepper from '../../../Components/Stepper/Vertical/VerticalLinearStepper';
import { Grow } from '@material-ui/core';
import { InvoiceHelpType } from '../../../Enums/invoices';
import { logoutUser } from '../../../Utils/User/Actions';
import ContractAssociationNoCheckedModalGDC from './ContractAssociationNoCheckedModal.GDC';
import ContractAssociationNoCheckedModalConfirmGDC from './ContractAssociationNoCheckedModalConfirm.GDC';
import { WrongContractDataModals } from './ContractAssociation';
import { ROUTE_NAMES } from '../../../Routes/Routes';
import { DejalaIrContext } from '../../../Contexts/DejalaIrContext';
import { checkConstructorContract } from '../../../Utils/Contract/checkConstructorContract';
import { openDigitalInvoiceThanksPage } from '../../../Utils/Contract/openDigitalInvoiceThanksPage';
import SweetAlert from '../../../Components/Alerts/SweetAlert';
import { ALERT_TYPE } from '../../../Components/Alerts/alert_enums';
import { useCaptchaWithFallback } from '../../../Hooks/useCaptchaWithFallback';

const associateSchema = yup.object({
  aliasName: yup
    .string()
    .trim()
    .max(12, 'Debe tener 12 letras o menos.')
    .required('Ingresa un alias'),
  aliasIcon: yup.number().required('Selecciona un ícono'),
  referredCode: yup.string().when('$isReferredCodeRequired', {
    is: true,
    then: schema =>
      schema
        .trim()
        .matches(/^[a-zA-Z0-9]*$/, 'El campo debe ser alfanumérico')
        .max(50, 'Debe tener 50 caracteres o menos.')
        .required('Ingresa un código'),
    otherwise: schema => schema.trim().default('')
  }),
  contractRelationship: yup
    .string()
    .trim()
    .oneOf([...CONTRACT_RELATIONSHIPS.map(({ value }) => value)])
    .required('Selecciona la relación con el predio')
});

const DEFAULT_CONTRACT_CHALLENGE = {
  challengeId: null,
  tasks: [],
  type: null,
  status: null,
  answers: []
};

const AssociationStep = {
  Query: 0,
  Validation: 1,
  Preferences: 2
};

const AssociationLabelSteps = {
  [AssociationStep.Query]: 'Consulta tu contrato',
  [AssociationStep.Validation]: 'Valida tu contrato',
  [AssociationStep.Preferences]: 'Elige tus preferencias'
};

const ContractAssociationGDC = props => {
  const { location: locationParams = { state: null } } = props;
  const params = locationParams.state;

  // * CONTEXTS
  const currentUser = useContext(UserContext);
  const setCurrentUser = useContext(UserDispatchContext);
  const refreshContracts = useContext(ContractsRefreshContext);
  const setAlert = useContext(AlertsDispatchContext);
  const userContracts = useContext(ContractsContext);
  const { validateCampaign } = useContext(DejalaIrContext);
  // * STATE HOOKS
  const [aliasIcon, setAliasIcon] = useState(1);
  const [aliasName, setAliasName] = useState('');
  const [contractRelationship, setContractRelationship] = useState('');
  const [sendInvoice, setSendInvoice] = useState(true);
  const [digitalInvoice, setDigitalInvoice] = useState();
  const [digitalChannels, setDigitalChannels] = useState({
    email: true,
    whatsapp: false,
    sms: false
  });
  const [isReferredCodeRequired, setIsReferredCodeRequired] = useState(false);
  const [referredCode, setReferredCode] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [number, setNumber] = useState((params && params.contractId) || '');
  const [loadingContract, setLoadingContract] = useState(false);
  const [contract, setContract] = useState(null);
  const [isContract, setIsContract] = useState(false);
  const [helpTypeDialog, setHelpTypeDialog] = useState(null);
  const [needsPhoneValidation, setNeedsPhoneValidation] = useState(false);
  const [openPhoneValidationDialog, setOpenPhoneValidationDialog] = useState(
    false
  );
  const [isDisclaimerReady, setIsDisclaimerReady] = useState(false);
  const [phoneActionText, setPhoneActionText] = useState('Validar');
  const [contractChallenge, setContractChallenge] = useState(
    DEFAULT_CONTRACT_CHALLENGE
  );
  const [activeStep, setActiveStep] = useState(AssociationStep.Query);
  const [redirectPage, setRedirectPage] = useState('');
  const [openNoCheckedDataModal, setOpenNoCheckedDataModal] = useState(false);
  const [
    openNoCheckedDataConfirmModal,
    setOpenNoCheckedDataConfirmModal
  ] = useState(false);
  const [phoneValidationTask, setPhoneValidationTask] = useState(false);
  const [
    openWhatsAppVerificationDialog,
    setOpenWhatsAppVerificationDialog
  ] = useState(false);

  // * OTHER HOOKS
  const classes = useStyles();

  const {
    captchaRef: getChallengeCaptchaRef,
    handlers: getChallengeCaptchaHandlers,
    getTokenWithFallback: getChallengeToken
  } = useCaptchaWithFallback();

  const {
    captchaRef: associateCaptchaRef,
    handlers: associateCaptchaHandlers,
    getTokenWithFallback: getAssociateToken
  } = useCaptchaWithFallback();

  const isContractRef = useRef(null);
  const [phoneInputValues, setPhoneInputValues] = useState({
    phoneCountryCode: (currentUser && currentUser.phoneCountryCode) || '57',
    phone: (currentUser && currentUser.phone) || '',
    isValid: false
  });

  const isConstructorContract = useMemo(
    () => checkConstructorContract(contract),
    [contract]
  );

  const onPhoneValuesChange = useCallback(values => {
    setPhoneInputValues(values);
  }, []);

  const isIndustrialContract = useMemo(
    () =>
      contract &&
      (contract.type === CONTRACT_TYPES.Industrial ||
        contract.type === CONTRACT_TYPES.Unregulated),
    [contract]
  );

  const isRequestContractAssociation = useMemo(
    () =>
      (contract && contract.type === CONTRACT_TYPES.Unregulated) ||
      isConstructorContract,
    [contract, isConstructorContract]
  );

  const allowedContractType = isDigitalInvoiceAllowedByContractType(contract);

  const customUserPhoneValidation = useMemo(() => {
    if (!contractChallenge.tasks.length || !contractChallenge.answers) {
      return null;
    }

    const hasPhoneValidationTask = contractChallenge.tasks.find(
      t => t.id === TaskQuestionType.PhoneValidation
    );

    if (!hasPhoneValidationTask) {
      return null;
    }

    const phoneValidationAnswer = contractChallenge.answers.find(
      answer => answer.id === TaskQuestionType.PhoneValidation
    );

    if (!phoneValidationAnswer || !phoneValidationAnswer.answer) {
      return null;
    }

    const { phone, phoneCountryCode } = phoneValidationAnswer.answer;

    if (phone && phoneCountryCode && !currentUser.phoneValidated) {
      return {
        ...currentUser,
        phone,
        phoneCountryCode,
        phoneValidated: currentUser.phoneValidated || false
      };
    }

    return null;
  }, [currentUser, contractChallenge]);

  // Reuse the phone answered in the challenge so the preferences step does not ask for it again.
  const shouldShowPhoneInputPreferences =
    !(currentUser && currentUser.phone) &&
    !(currentUser && currentUser.phoneValidated) &&
    !customUserPhoneValidation;

  // * LAYOUT EFFECTS
  useLayoutEffect(() => {
    // Scroll to "is this your contract" Toggle button
    if (contract && isContractRef.current) {
      isContractRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [contract]);

  useEffect(() => {
    if (!currentUser) {
      history.replace('/');
      return;
    }
  }, [currentUser]);

  useEffect(() => {
    if (!isContract) {
      setCanSubmit(false);
      return;
    }

    try {
      associateSchema.validateSync(
        {
          aliasName,
          aliasIcon,
          referredCode,
          contractRelationship
        },
        {
          context: {
            isReferredCodeRequired
          }
        }
      );
      setErrors({});

      if (shouldShowPhoneInputPreferences && !phoneInputValues.isValid) {
        setCanSubmit(false);
        return;
      }

      // Digital invoice gate: enabled button when not applicable, already active, or explicitly answered
      const hasResolvedDigitalInvoice =
        !allowedContractType ||
        Boolean(contract && contract.digitalInvoice) ||
        typeof digitalInvoice === 'boolean';

      setCanSubmit(hasResolvedDigitalInvoice);
    } catch (err) {
      setCanSubmit(false);
      setErrors({
        [err.path]: err.errors[0]
      });
    }
  }, [
    isContract,
    aliasName,
    aliasIcon,
    sendInvoice,
    digitalInvoice,
    setDigitalInvoice,
    referredCode,
    isReferredCodeRequired,
    contractRelationship,
    contract,
    activeStep,
    isConstructorContract,
    isIndustrialContract,
    phoneInputValues,
    shouldShowPhoneInputPreferences,
    allowedContractType
  ]);

  useEffect(() => {
    if (contract) {
      setAliasIcon(
        getDefaultContractIcon(contract.type, isConstructorContract)
      );
      setAliasName(
        getDefaultContractAlias(contract.type, isConstructorContract)
      );
    }
  }, [contract, isConstructorContract]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    if (!currentUser.phone && !currentUser.phoneCountryCode) {
      setPhoneActionText('Agregar');
      return;
    }

    if (!currentUser.phoneValidated) {
      setPhoneActionText('Confirmar');
    }
  }, [currentUser]);

  const onChangeAliasName = useCallback(value => {
    setAliasName(value);
    setErrors(errs => ({ ...errs, aliasName: null }));
  }, []);

  const onChangeReferredCode = useCallback(value => {
    setReferredCode(value.toUpperCase());
    setErrors(errs => ({ ...errs, referredCode: null }));
  }, []);

  const resetDigitalInvoiceForm = useCallback(() => {
    setSendInvoice(true);
    setDigitalChannels({ email: true, whatsapp: false, sms: false });
    setReferredCode('');
    setDigitalInvoice();
  }, []);

  const resetChallenge = useCallback(
    (challenge = true) => {
      if (challenge) {
        setContractChallenge(DEFAULT_CONTRACT_CHALLENGE);
      }
    },
    [setContractChallenge]
  );

  // * QUERY SINGLE CONTRACT
  const _resetQueryData = useCallback(() => {
    setContract(null);
    setLoadingContract(false);
    setIsContract(false);
    resetDigitalInvoiceForm();
    resetChallenge(true);
  }, [resetDigitalInvoiceForm, resetChallenge]);

  useEffect(() => {
    _resetQueryData();
  }, [number, _resetQueryData]);

  const _handleQuery = useCallback(
    async event => {
      event.preventDefault();
      _resetQueryData();

      if (userContracts.find(({ id }) => id === parseInt(number, 10))) {
        setAlert({
          type: 'error',
          message: `${Company.contractConjugation.capitalized.singular.article} ya está asociado a tu cuenta`
        });
        return;
      }

      setLoadingContract(true);
      resetChallenge();

      const recaptchaToken = await getChallengeToken();

      const challengeResponse = await GetContractChallengeAPI(
        {
          contractId: Number(number),
          contractRelationship
        },
        currentUser.token,
        recaptchaToken
      );

      if (challengeResponse.success) {
        const challenge = challengeResponse.data.data;

        const { tasks, type } = challenge;
        const isValidChallenge = isChallengeTypeValid(type);

        if (isValidChallenge) {
          const areValidTasks = areTasksValid(tasks, type);

          if (areValidTasks) {
            setContractChallenge({
              ...challenge,
              status: ChallengeStatus.ToValidate
            });

            const requiresPhoneValidation = tasks.some(
              task => task.id === TaskQuestionType.PhoneValidation
            );

            setPhoneValidationTask(requiresPhoneValidation);
          }
        }
      } else {
        setContract(null);
        setLoadingContract(false);
        if (
          redirectOnAuthFailure(challengeResponse, '/', () =>
            logoutUser(setCurrentUser)
          )
        ) {
          return;
        }

        setAlert({
          type: 'error',
          message: extractErrorMessage(challengeResponse).message
        });

        return;
      }

      const response = await GetSingleContractAPI(number, currentUser.token);

      if (response.success) {
        setLoadingContract(false);
        setContract(response.data.data);
      } else {
        setLoadingContract(false);
        if (
          redirectOnAuthFailure(response, '/', () => logoutUser(setCurrentUser))
        ) {
          return;
        }

        setAlert({
          type: 'error',
          message: extractErrorMessage(response).message
        });
      }
    },
    [
      _resetQueryData,
      currentUser,
      number,
      setCurrentUser,
      setAlert,
      userContracts,
      contractRelationship,
      resetChallenge,
      getChallengeToken
    ]
  );

  const checkPhoneValidationTask = useCallback(() => {
    // Si hay reto de validación de teléfono pero el teléfono ya está validado, no se necesita validar
    if (phoneValidationTask && currentUser.phoneValidated) {
      return false;
    }

    // Si hay reto de telefono y el telefono no esta validado, debe validarse
    if (phoneValidationTask && !currentUser.phoneValidated) {
      return true;
    }

    return false;
  }, [phoneValidationTask, currentUser]);

  // Sends a WhatsApp-first OTP and opens the code dialog to certify the
  // phone for the WhatsApp digital-invoice channel
  const openWhatsAppRevalidation = useCallback(async () => {
    setLoading(true);
    const resendResponse = await ResendPhoneConfirmationAPI(currentUser.token, {
      whatsapp: true
    });

    if (!resendResponse.success) {
      setAlert({
        type: ALERT_TYPE.ERROR,
        message:
          extractErrorMessage(resendResponse).message ||
          'Error al enviar el código'
      });
      setLoading(false);
      return { unmounting: false, closeDialog: false };
    }

    setOpenWhatsAppVerificationDialog(true);
    setLoading(false);
    return { unmounting: false, closeDialog: false };
  }, [currentUser.token, setAlert, setOpenWhatsAppVerificationDialog]);

  // * ASSOCIATE CONTRACT
  const _handleAssociate = useCallback(async () => {
    setLoading(true);
    setCanSubmit(false);

    const contractHasDigitalInvoice = contract.digitalInvoice;

    // If the contract has an active digital invoice,
    // the user's response is taken as sendInvoice.
    // If it does not have it active, sendInvoice will
    // be the same as digitalInvoice.

    const realSendInvoice = contractHasDigitalInvoice
      ? sendInvoice
      : digitalInvoice;

    const realDigitalInvoice = contractHasDigitalInvoice || digitalInvoice;

    const digitalInvoiceSettings = {
      digitalInvoice: realDigitalInvoice,
      sendInvoice: realSendInvoice,
      sendEmailInvoice: realSendInvoice,
      sendWhatsAppInvoice: realSendInvoice && digitalChannels.whatsapp,
      sendSmsInvoice: realSendInvoice && digitalChannels.sms
    };

    // When the contract to associate is not an
    // allowed one for digital invoice, it sets
    // every digital invoice setting to false
    if (!allowedContractType) {
      for (const key of Object.keys(digitalInvoiceSettings)) {
        digitalInvoiceSettings[key] = false;
      }
    }

    const newAssociation = {
      ...digitalInvoiceSettings,
      alias: aliasName.trim(),
      icon: aliasIcon,
      referredCode,
      origin: {
        url: window.location.pathname
      },
      contractRelationship,
      wrongContractInformationData: redirectPage
        ? {
            data:
              redirectPage === ROUTE_NAMES.ownership ? 'ownership' : 'address'
          }
        : {}
    };

    const { challengeId, answers: userAnswers } = contractChallenge;

    if (challengeId) {
      const answers = formatAnswerDataBody(
        userAnswers,
        false,
        contractChallenge.type
      );

      newAssociation.challengeData = {
        challengeId,
        answers
      };
    }

    const recaptchaToken = await getAssociateToken();

    const response = await PostAssociateContract(
      currentUser.token,
      number,
      newAssociation,
      recaptchaToken
    );

    if (response.success) {
      let redirect;
      let alertMessage;

      if (isRequestContractAssociation) {
        redirect = ROUTE_NAMES.requests;
        alertMessage = `Se ha creado la solicitud #${
          response.data.data.requestId
        } para el ${
          Company.contractConjugation.regular.singular.article
        } "${aliasName.trim()}"`;
      } else {
        redirect = ROUTE_NAMES.home;
        alertMessage = `Se ha asociado ${
          Company.contractConjugation.regular.singular.article
        } "${aliasName.trim()}"`;

        await openDigitalInvoiceThanksPage({
          wasDigitalInvoiceActive: contract.digitalInvoice,
          shouldRedirect: digitalInvoice || realSendInvoice,
          contractId: contract.id,
          validateCampaign
        });
      }

      setAlert({
        type: ALERT_TYPE.SUCCESS,
        message: alertMessage
      });

      history.push(redirectPage || redirect);

      if (!isRequestContractAssociation) {
        refreshContracts();
      }

      return { unmounting: true };
    }

    if (
      redirectOnAuthFailure(response, '/', () => logoutUser(setCurrentUser))
    ) {
      return { unmounting: true };
    }

    const errorCode = extractErrorCode(response).code;
    if (errorCode && errorCode.includes('CHECK-CONTRACT-CHALLENGE')) {
      setActiveStep(AssociationStep.Validation);
    }

    if (errorCode && errorCode === 'WHATSAPP-PHONE-VALIDATION-REQUIRED') {
      return openWhatsAppRevalidation();
    }

    setAlert({
      type: 'error',
      message: extractErrorMessage(response).message
    });
    setLoading(false);

    return { unmounting: false, closeDialog: true };
  }, [
    number,
    aliasName,
    aliasIcon,
    digitalInvoice,
    digitalChannels,
    sendInvoice,
    contract,
    referredCode,
    allowedContractType,
    contractChallenge,
    setAlert,
    setCurrentUser,
    refreshContracts,
    currentUser,
    validateCampaign,
    contractRelationship,
    redirectPage,
    isRequestContractAssociation,
    getAssociateToken,
    setOpenPhoneValidationDialog,
    openWhatsAppRevalidation
  ]);

  // OTHER FUNCTIONS
  const closePhoneDialog = useCallback(() => {
    setOpenPhoneValidationDialog(false);
  }, []);

  const contractYesChecked = useCallback(() => {
    setIsContract(true);

    let nextStep = AssociationStep.Preferences;
    if (contractChallenge.status === ChallengeStatus.ToValidate) {
      nextStep = AssociationStep.Validation;
    }

    setActiveStep(nextStep);
  }, [contractChallenge.status]);

  const controlWrongContractDataModal = useCallback(
    (modal, value) => {
      if (modal === WrongContractDataModals.SelectOptions) {
        setOpenNoCheckedDataModal(value || !openNoCheckedDataModal);
      }
      if (modal === WrongContractDataModals.Confirm) {
        setOpenNoCheckedDataConfirmModal(
          value || !openNoCheckedDataConfirmModal
        );
      }
    },
    [
      openNoCheckedDataModal,
      setOpenNoCheckedDataModal,
      openNoCheckedDataConfirmModal,
      setOpenNoCheckedDataConfirmModal
    ]
  );

  const contractNoChecked = useCallback(() => {
    if (isContract) {
      setIsContract(false);
    }
    controlWrongContractDataModal(WrongContractDataModals.SelectOptions, true);
  }, [isContract, controlWrongContractDataModal]);

  const onHelpClick = useCallback(type => {
    setHelpTypeDialog(type);
  }, []);

  const onSubmitAssociate = useCallback(() => {
    const requiresPhoneValidationByTask = checkPhoneValidationTask();
    const havePhoneToValidate =
      phoneInputValues.isValid && Boolean(phoneInputValues.phone);

    const isPhoneValidationNeeded =
      needsPhoneValidation ||
      requiresPhoneValidationByTask ||
      havePhoneToValidate;

    if (isPhoneValidationNeeded) {
      setOpenPhoneValidationDialog(true);
      return;
    }

    // Reset phoneValidationTask una vez que se confirma que no se necesita validación
    if (!requiresPhoneValidationByTask && phoneValidationTask) {
      setPhoneValidationTask(false);
    }

    _handleAssociate();
  }, [
    needsPhoneValidation,
    _handleAssociate,
    checkPhoneValidationTask,
    phoneValidationTask,
    phoneInputValues.isValid,
    phoneInputValues.phone
  ]);

  const saveChallengeUserAnswer = useCallback(answers => {
    setContractChallenge(c => ({
      ...c,
      answers
    }));

    setActiveStep(AssociationStep.Preferences);
  }, []);

  const stepperSteps = useMemo(() => {
    const usedSteps = [
      {
        label: `Asociar ${Company.contractConjugation.regular.singular.newUndefinedArticle}`,
        onBack: () => history.goBack(),
        action: { text: 'Asociar', onCall: onSubmitAssociate }
      }
    ];

    return usedSteps;
  }, [onSubmitAssociate]);

  const disableQueryButton =
    loading || !isDisclaimerReady || !number || !contractRelationship;

  const phoneDialogCustomUser = useMemo(() => {
    if (customUserPhoneValidation) {
      return customUserPhoneValidation;
    }

    if (phoneInputValues.isValid && phoneInputValues.phone) {
      return {
        ...currentUser,
        phone: phoneInputValues.phone,
        phoneCountryCode: phoneInputValues.phoneCountryCode
      };
    }

    return null;
  }, [customUserPhoneValidation, phoneInputValues, currentUser]);

  const renderQueryContractStep = useCallback(() => {
    return (
      <>
        <form onSubmit={_handleQuery}>
          <Grid container className={classes.inputsContainer}>
            <Grid item sm={4} xs={12}>
              <TextInput
                autoFocus
                fullWidth
                value={number}
                onChange={e => setNumber(e.target.value)}
                disabled={loading}
                label={`Número de ${Company.contractConjugation.regular.singular.main}`}
                InputProps={{
                  endAdornment: (
                    <QuestionMark
                      onClick={() => onHelpClick(InvoiceHelpType.Contract)}
                    />
                  ),
                  inputProps: {
                    type: 'number',
                    inputMode: 'numeric'
                  }
                }}
                required={true}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <SelectInput
                className={classes.contractRelationshipInput}
                value={contractRelationship}
                onChange={e => setContractRelationship(e.target.value)}
                disabled={loading}
                label="Relación con el predio*"
                options={CONTRACT_RELATIONSHIPS}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <BaseButton
                disabled={disableQueryButton}
                fullWidth={true}
                color="primary"
                variant="outlined"
                type="submit"
                className={classes.button}
              >
                Consultar
              </BaseButton>
            </Grid>
          </Grid>
          <div className={classes.disclaimerContainer}>
            <RecaptchaDisclaimer onReady={setIsDisclaimerReady} />
          </div>
        </form>
        <ContractCard
          data={contract}
          className={classes.contract}
          loading={loadingContract}
        />
        {contract && (
          <Grow in>
            <div className={classes.itemContainer}>
              <Typography className={classes.itemText} ref={isContractRef}>
                ¿La información visualizada{' '}
                {Company.contractConjugation.regular.singular.contraction} es la
                correcta?
              </Typography>
              <YesNoButton
                checked={isContract}
                variant="outlined"
                yesChecked={contractYesChecked}
                noChecked={contractNoChecked}
                color="primary"
                small="small"
              />
            </div>
          </Grow>
        )}
      </>
    );
  }, [
    _handleQuery,
    classes.button,
    classes.contract,
    classes.contractRelationshipInput,
    classes.disclaimerContainer,
    classes.inputsContainer,
    classes.itemContainer,
    classes.itemText,
    contract,
    contractNoChecked,
    contractRelationship,
    contractYesChecked,
    disableQueryButton,
    isContract,
    loading,
    isDisclaimerReady,
    loadingContract,
    number,
    onHelpClick
  ]);

  const renderValidationStep = useCallback(() => {
    if (!contract) {
      return <></>;
    }

    return (
      <ContractChallenge
        tasks={contractChallenge.tasks}
        challengeId={contractChallenge.challengeId}
        type={contractChallenge.type}
        contractId={contract.id}
        handleAnswers={saveChallengeUserAnswer}
        setContractChallenge={setContractChallenge}
        contract={contract}
        setInvoiceHelp={onHelpClick}
      />
    );
  }, [
    contractChallenge.challengeId,
    contractChallenge.tasks,
    contractChallenge.type,
    contract,
    saveChallengeUserAnswer,
    onHelpClick
  ]);

  const preferencesStep = useCallback(() => {
    if (!contract || !isContract) {
      return <></>;
    }

    return (
      <ContractAssociationForm
        currentUser={currentUser}
        contract={contract}
        sendInvoice={sendInvoice}
        setSendInvoice={setSendInvoice}
        isDigitalInvoice={digitalInvoice}
        setIsDigitalInvoice={setDigitalInvoice}
        digitalChannels={digitalChannels}
        setDigitalChannels={setDigitalChannels}
        aliasName={aliasName}
        setAliasName={onChangeAliasName}
        aliasIcon={aliasIcon}
        setAliasIcon={setAliasIcon}
        errors={errors}
        needsPhoneValidation={needsPhoneValidation}
        setNeedsPhoneValidation={setNeedsPhoneValidation}
        setCanSubmit={setCanSubmit}
        referredCode={referredCode}
        isReferredCodeRequired={isReferredCodeRequired}
        setIsReferredCodeRequired={setIsReferredCodeRequired}
        setReferredCode={onChangeReferredCode}
        referredCodeEnable
        allowedContractType={allowedContractType}
        redirectPage={redirectPage}
        onPhoneValuesChange={onPhoneValuesChange}
        showPhoneInputPreferences={!customUserPhoneValidation}
        phoneInputValues={phoneInputValues}
      />
    );
  }, [
    aliasIcon,
    aliasName,
    allowedContractType,
    contract,
    currentUser,
    digitalChannels,
    digitalInvoice,
    errors,
    isContract,
    needsPhoneValidation,
    onChangeAliasName,
    onChangeReferredCode,
    onPhoneValuesChange,
    referredCode,
    isReferredCodeRequired,
    setIsReferredCodeRequired,
    sendInvoice,
    redirectPage
  ]);

  const verticalSteps = useMemo(() => {
    let nextButtonFormId = 'validation-step-next-button';

    if (contractChallenge.challengeId) {
      nextButtonFormId = `challenge-form-${contractChallenge.challengeId}`;
    }

    const steps = [
      {
        step: AssociationStep.Query,
        label: AssociationLabelSteps[AssociationStep.Query],
        content: renderQueryContractStep()
      },
      {
        step: AssociationStep.Validation,
        label: AssociationLabelSteps[AssociationStep.Validation],
        content: renderValidationStep(),
        showBack: true,
        onClickBack: () => {
          setIsContract(false);
          setActiveStep(AssociationStep.Query);
        },
        nextLabel: 'Aceptar',
        showNext: true,
        nextButtonFormId
      },
      {
        step: AssociationStep.Preferences,
        label: AssociationLabelSteps[AssociationStep.Preferences],
        content: preferencesStep(),
        showBack: true,
        onClickBack: () => {
          setIsContract(false);
          setActiveStep(AssociationStep.Query);
          resetDigitalInvoiceForm();
        }
      }
    ];

    return steps;
  }, [
    renderQueryContractStep,
    renderValidationStep,
    preferencesStep,
    resetDigitalInvoiceForm,
    contractChallenge.challengeId
  ]);

  return (
    <div className={classes.root}>
      <TurnstileInvisibleCaptcha
        captchaRef={getChallengeCaptchaRef}
        onResolved={getChallengeCaptchaHandlers.onResolved}
        onExpired={getChallengeCaptchaHandlers.onExpired}
        onError={getChallengeCaptchaHandlers.onError}
        onLoaded={getChallengeCaptchaHandlers.onLoaded}
        action={RecaptchaAction.AssociateContractGetChallenge}
      />
      <TurnstileInvisibleCaptcha
        captchaRef={associateCaptchaRef}
        onResolved={associateCaptchaHandlers.onResolved}
        onExpired={associateCaptchaHandlers.onExpired}
        onError={associateCaptchaHandlers.onError}
        onLoaded={associateCaptchaHandlers.onLoaded}
        action={RecaptchaAction.AssociateContract}
      />
      {openPhoneValidationDialog && (
        <RequestDialog
          open={openPhoneValidationDialog}
          setDialog={setOpenPhoneValidationDialog}
          beforeClosing={closePhoneDialog}
          requestTitle={`${phoneActionText} número celular`}
          requestCallback={_handleAssociate}
          withoutObservation={true}
          backButton={{
            text: 'Cancelar',
            handle: closePhoneDialog
          }}
          submitButtonText="Continuar"
          requestNode={
            <Typography paragraph className={classes.text}>
              {`Para llevar a cabo esta acción es necesario ${phoneActionText.toLowerCase()} tu número de celular`}
            </Typography>
          }
          contentSize="small"
          customUser={phoneDialogCustomUser}
          otpChannels={{
            sms: digitalChannels.sms,
            whatsapp: digitalChannels.whatsapp
          }}
        />
      )}
      {openWhatsAppVerificationDialog && (
        <PhoneVerificationDialog
          open={openWhatsAppVerificationDialog}
          setDialog={setOpenWhatsAppVerificationDialog}
          setAlert={setAlert}
          title="Validar número celular"
          otpChannels={{ whatsapp: true }}
          successCallback={_handleAssociate}
        />
      )}
      <InvoiceHelpDialog
        open={Boolean(helpTypeDialog)}
        type={helpTypeDialog}
        onClose={() => setHelpTypeDialog(null)}
        title={
          helpTypeDialog === InvoiceHelpType.Invoice
            ? HELP_TEXT.invoice
            : HELP_TEXT.contract
        }
      />
      {loading && <FullSizeProgress />}
      {loadingContract && <ModalProgress />}
      <Container maxWidth="md">
        <Title
          text={`Asociar ${Company.contractConjugation.regular.singular.newUndefinedArticle}`}
        />
        <VerticalLinearStepper steps={verticalSteps} activeStep={activeStep} />
        {isRequestContractAssociation && canSubmit && (
          <SweetAlert
            message={
              'Al hacer clic en el botón "Asociar" se creará una solicitud de asociación que debe ser revisada por un encargado.'
            }
          />
        )}
      </Container>
      <FooterPayment currentStep={stepperSteps[0]} disabledNext={!canSubmit} />
      <ContractAssociationNoCheckedModalGDC
        open={openNoCheckedDataModal}
        redirectPage={redirectPage}
        setRedirectPage={setRedirectPage}
        contractType={contract && contract.type}
        isConstructorContract={isConstructorContract}
        isIndustrialContract={isIndustrialContract}
        controlWrongContractDataModal={controlWrongContractDataModal}
        contractYesChecked={contractYesChecked}
      />
      <ContractAssociationNoCheckedModalConfirmGDC
        open={openNoCheckedDataConfirmModal}
        redirectPage={redirectPage}
        setRedirectPage={setRedirectPage}
        controlWrongContractDataModal={controlWrongContractDataModal}
        contractYesChecked={contractYesChecked}
      />
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingTop: theme.spacing(3),
    marginBottom: theme.custom.footerHeight.stepper,
    paddingBottom: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      paddingTop: theme.spacing(6)
    }
  },
  inputsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    [theme.breakpoints.up(theme.breakpoints.values.sm)]: {
      '& > *:first-child': {
        paddingRight: theme.spacing(2)
      },
      '& > *:last-child': {
        paddingLeft: theme.spacing(2)
      }
    }
  },
  button: {
    marginTop: theme.spacing(2),
    paddingTop: 20,
    paddingBottom: 20
  },
  contractContainer: {
    marginTop: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(2),
      paddingLeft: theme.spacing(2)
    }
  },
  itemContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.up(theme.breakpoints.values.sm)]: {
      flexDirection: 'row'
    }
  },
  itemText: {
    fontSize: 14,
    fontWeight: 500,
    paddingLeft: theme.spacing(),
    paddingRight: theme.spacing(4),
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.up(theme.breakpoints.values.sm)]: {
      paddingBottom: 0
    }
  },
  itemDivider: {
    marginTop: 0,
    marginBottom: 0
  },
  disclaimerContainer: {
    marginTop: theme.spacing(),
    marginBottom: theme.spacing()
  },
  contractRelationshipInput: {
    width: '100%'
  }
}));

export default ContractAssociationGDC;
